import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';

export type FilledRosterAssignment = {
  seatName: string;
  personKind: 'user' | 'contact';
  personId: string;
  name: string;
  email: string;
  phone: string;
  userGuid: string | null;
};

export type FilledRosterTemplate = {
  id: string;
  groupGuid: string;
  name: string;
  sourceTemplateId: string | null;
  sourceSlug: string | null;
  assignments: FilledRosterAssignment[];
};

export type FilledRosterWrite = {
  name: string;
  sourceTemplateId?: string | null;
  sourceSlug?: string | null;
  assignments: FilledRosterAssignment[];
};

export const filledRosterTemplateKeys = {
  list: (groupGuid: string) => ['filled-roster-templates', groupGuid] as const,
};

export function useFilledRosterTemplates(options?: { enabled?: boolean }) {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery({
    queryKey: filledRosterTemplateKeys.list(groupGuid ?? ''),
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get<FilledRosterTemplate[]>(
        config.apiGateway.filledRosterTemplates,
        { params: { groupGuid } }
      );
      return response.data ?? [];
    },
  });
}

export function useSaveFilledRosterTemplate() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: FilledRosterWrite & { id?: string }) => {
      const body = { ...input, groupGuid };
      if (input.id) {
        const response = await axios.put<FilledRosterTemplate>(
          `${config.apiGateway.filledRosterTemplates}/${input.id}`,
          body
        );
        return response.data;
      }
      const response = await axios.post<FilledRosterTemplate>(
        config.apiGateway.filledRosterTemplates,
        body
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: filledRosterTemplateKeys.list(groupGuid ?? ''),
      });
    },
    onError: () => {
      toast.error('Could not save the filled roster template');
    },
  });
}

export function useArchiveFilledRosterTemplate() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${config.apiGateway.filledRosterTemplates}/${id}`, {
        params: { groupGuid },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: filledRosterTemplateKeys.list(groupGuid ?? ''),
      });
    },
    onError: () => {
      toast.error('Could not remove the filled roster template');
    },
  });
}
